import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getPatientKey } from "@/store/profile";
import { useTaskStore } from "@/store/task";
import taskRequest from "@/api/task-request";
import { isActiveInfoTaskItem } from "@/types/tasks";
import { navigateToFormHandler } from "@/utils/formHandlerLaunch";
import {
  buildFirstFormHandlerLaunchPayload,
  loadPendingFormTasks,
} from "@/utils/navigateToFormHandler";

const HOME_NAV_DELAY_MS = 3000;

export type InfoTaskCompletionResult =
  | { kind: "skipped" }
  | { kind: "failed" }
  | { kind: "thank_you" }
  | { kind: "opened_forms" };

/** Inline message after a history/info portal task is marked complete (no modal). */
export function InfoTaskCompletionThankYou() {
  return (
    <p className="py-10 text-center text-[15px] font-semibold text-[#1E252B] leading-relaxed px-2">
      Thank you for completing the information!
    </p>
  );
}

/**
 * Call after the last wizard step for an active isHistory portal task:
 * UpdateCaseHistoryComplete, then UpdateTaskComplete, clear task from store,
 * show thank-you, then navigate home.
 * Render `InfoTaskCompletionThankYou` when `showThankYou` is true.
 */
export function useInfoTaskWizardCompletion() {
  const navigate = useNavigate();
  const setTaskItem = useTaskStore((s) => s.setTaskItem);
  const [showThankYou, setShowThankYou] = useState(false);
  const navigateAfterThanksRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (navigateAfterThanksRef.current != null) {
        clearTimeout(navigateAfterThanksRef.current);
        navigateAfterThanksRef.current = null;
      }
    };
  }, []);

  const completeActiveInfoTask = useCallback(
    async (facilityKey: string): Promise<InfoTaskCompletionResult> => {
      const taskItem = useTaskStore.getState().taskItem;
      if (!isActiveInfoTaskItem(taskItem)) {
        return { kind: "skipped" };
      }
      try {
        const caseHistoryId = useTaskStore.getState().caseHistory?.id;
        if (caseHistoryId == null || caseHistoryId <= 0) {
          return { kind: "failed" };
        }
        const patientKey = getPatientKey();
        await taskRequest.request.updateCaseHistoryComplete(
          facilityKey,
          patientKey,
          caseHistoryId
        );
        await taskRequest.request.updateTaskComplete(
          facilityKey,
          patientKey,
          taskItem.taskID
        );
        setTaskItem(undefined);

        const formTasks = await loadPendingFormTasks(facilityKey, patientKey);
        const payload = buildFirstFormHandlerLaunchPayload(formTasks);
        if (payload) {
          navigateToFormHandler(navigate, payload, { replace: true });
          return { kind: "opened_forms" };
        }

        setShowThankYou(true);
        navigateAfterThanksRef.current = setTimeout(() => {
          navigateAfterThanksRef.current = null;
          navigate("/");
        }, HOME_NAV_DELAY_MS);
        return { kind: "thank_you" };
      } catch {
        return { kind: "failed" };
      }
    },
    [navigate, setTaskItem]
  );

  return { showThankYou, completeActiveInfoTask };
}
